import { TFunction } from "i18next";
import * as Yup from "yup";

import { isValidIban, looksLikeIban } from "../../../../formik/formik-iban-field/FormikIbanField.utils";
import { nullableAmount, nullableDate, nullableText } from "../../utils/validation.utils";
import { AssetsFormValues, AssetsMaxLength, FinancialHoldingFormValue } from "./AssetsView.types";
import { FINANCIAL_HOLDING_KINDS } from "./AssetsView.utils";

const DETAIL_FIELDS = [
  "domesticRealEstateDetails",
  "foreignRealEstateDetails",
  "safeDepositDetails",
  "otherCashAssetsDetails",
  "valuablesDetails",
  "furnishingsDetails",
  "appliancesDetails",
  "equipmentDetails",
  "vehiclesDetails",
  "buildingsOnForeignLandDetails",
  "otherItemsDetails",
  "itemsOwnedByOthersDetails",
] as const;

function isHoldingShown(values: AssetsFormValues, holding: FinancialHoldingFormValue): boolean {
  const config = FINANCIAL_HOLDING_KINDS.find(({ kind }) => kind === holding.kind);
  return config !== undefined && values.answers[config.entryField] === true;
}

export function createAssetsSchema(t: TFunction) {
  const unchecked = () => Yup.mixed().nullable();

  const holdingSchema = (holding: FinancialHoldingFormValue) =>
    Yup.object().shape({
      institutionName: nullableText(t, AssetsMaxLength.institutionName),
      accountNumber: nullableText(t, AssetsMaxLength.accountNumber).test(
        "iban",
        t("common.iban.invalid"),
        (value) => !value || !looksLikeIban(value) || isValidIban(value)
      ),
      street: nullableText(t, AssetsMaxLength.street),
      houseNumber: nullableText(t, AssetsMaxLength.houseNumber),
      zipCode: nullableText(t, AssetsMaxLength.zipCode),
      city: nullableText(t, AssetsMaxLength.city),
      country: nullableText(t, AssetsMaxLength.country),
      value: holding.isValueKnown === true ? nullableAmount(t) : unchecked(),
      valueDate: holding.isValueKnown === true ? nullableDate(t) : unchecked(),
      remarks: nullableText(t, AssetsMaxLength.remarks),
    });

  return Yup.lazy((values: AssetsFormValues) => {
    const isSavedForProceeding =
      values.answers.hasOtherCashAssets === true && values.answers.isSavedForProceeding === true;

    return Yup.object().shape({
      answers: Yup.object().shape({
        ...Object.fromEntries(DETAIL_FIELDS.map((field) => [field, nullableText(t, AssetsMaxLength.details)])),
        savedForProceedingAmount: isSavedForProceeding ? nullableAmount(t) : unchecked(),
        savedForProceedingDate: isSavedForProceeding ? nullableDate(t) : unchecked(),
      }),
      financialHoldings: Yup.array().of(
        Yup.lazy((holding: FinancialHoldingFormValue) =>
          isHoldingShown(values, holding) ? holdingSchema(holding) : unchecked()
        )
      ),
    });
  });
}
